import React, { useEffect } from 'react';
import './Cosmos.css';

const Cosmos = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="cosmos-page">
            {/* Hero Section */}
            <section className="cosmos-hero section" data-aos="fade-up">
                <div className="container">
                    <div className="cosmos-hero-content">
                        <h1 className="cosmos-title">
                            COSMOS
                        </h1>
                    </div>
                </div>
            </section>

            {/* Why Execution Breaks Down - Centered Layout */}
            <section className="section cosmos-bg-light" style={{ padding: '2rem 0' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', maxWidth: '1000px', margin: '0 auto' }} data-aos="fade-up">
                        <span className="cosmos-section-label">The Execution System That Makes Strategy Work</span>
                        <h2 className="cosmos-section-title">Why Execution Breaks Down</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', textAlign: 'left', marginTop: '1.5rem' }}>
                            <div>
                                <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                                    Most organizations do not fail due to lack of ideas, strategy, or effort.
                                    They struggle because execution is left to individuals rather than governed as a system.
                                </p>
                                <p style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--primary-color)' }}>
                                    COSMOS was created to address exactly this gap.
                                </p>
                            </div>
                            <div className="cosmos-visual-col" style={{ background: '#fff', padding: '1.5rem 2rem' }}>
                                <h4 style={{ marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', fontSize: '1.1rem' }}>Common execution breakdowns:</h4>
                                <ul className="cosmos-list">
                                    <li>Unclear ownership and decision rights</li>
                                    <li>Initiatives competing for attention</li>
                                    <li>Progress tracked informally, if at all</li>
                                    <li>Leadership reviews that react instead of steer</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHAT IS COSMOS & WHY NECESSARY - 2 Column Grid */}
            <section className="section" style={{ padding: '6rem 0' }}>
                <div className="container">
                    <div className="cosmos-dual-grid">
                        {/* Left Column: WHAT IS COSMOS */}
                        <div className="cosmos-col" data-aos="fade-right">
                            <span className="cosmos-section-label">WHAT IS COSMOS?</span>
                            <h3 className="cosmos-section-title" style={{ fontSize: '2rem' }}>A Consulting & Project Management Operating System</h3>
                            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                                COSMOS is Triology’s Consulting & Project Management Operating System designed to bring structure, discipline, and visibility into execution.
                            </p>
                            <ul className="cosmos-list">
                                <li>It is not a software tool.</li>
                                <li>It is a way of working that governs how initiatives are planned, executed, reviewed, and course-corrected.</li>
                            </ul>
                            <p style={{ marginTop: '1.5rem', fontWeight: '700', fontSize: '1.2rem', color: 'var(--text-primary)' }}>
                                COSMOS ensures that strategy does not remain an intention it becomes consistent action.
                            </p>
                        </div>

                        {/* Right Column: WHY COSMOS IS NECESSARY */}
                        <div className="cosmos-col" data-aos="fade-left">
                            <span className="cosmos-section-label">WHY COSMOS IS NECESSARY</span>
                            <h3 className="cosmos-section-title" style={{ fontSize: '2rem' }}>Execution Needs Governance, Not Heroics</h3>
                            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                                Execution often depends on a few capable individuals holding things together. This works temporarily, but it does not scale.
                            </p>
                            <h6 style={{ marginBottom: '1.5rem', color: 'var(--primary-color)' }}>COSMOS shifts execution from:</h6>
                            <ul className="comparison-list" style={{ listStyle: 'none', padding: 0 }}>
                                <li>
                                    <span>Individual effort</span> <span className="arrow-separator">→</span> <span>Organizational discipline</span>
                                </li>
                                <li>
                                    <span>Informal follow-ups</span> <span className="arrow-separator">→</span> <span>Structured cadence</span>
                                </li>
                                <li>
                                    <span>Reactive firefighting</span> <span className="arrow-separator">→</span> <span>Proactive steering</span>
                                </li>
                            </ul>
                            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginTop: '1.5rem', lineHeight: '1.6' }}>
                                This creates reliability in execution, even as complexity increases.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW COSMOS WORKS - Grid */}
            <section className="section cosmos-bg-light" style={{ padding: '3rem 0' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '2rem' }} data-aos="fade-up">
                        <span className="cosmos-section-label">HOW COSMOS WORKS</span>
                        <h2 className="cosmos-section-title">The Core Elements of COSMOS</h2>
                        <p style={{ maxWidth: '700px', margin: '0 auto', fontSize: '1.1rem', color: 'var(--text-secondary)' }}>
                            COSMOS brings together five essential elements to create a stable execution rhythm.
                        </p>
                    </div>

                    <div className="cosmos-card-grid">
                        <div className="cosmos-card" data-aos="fade-up" data-aos-delay="0">
                            <h4>Clear Ownership</h4>
                            <p>Every initiative has defined accountability and decision rights.</p>
                        </div>
                        <div className="cosmos-card" data-aos="fade-up" data-aos-delay="100">
                            <h4>Milestone-Driven Planning</h4>
                            <p>Work is broken into achievable phases with visible outcomes.</p>
                        </div>
                        <div className="cosmos-card" data-aos="fade-up" data-aos-delay="200">
                            <h4>Execution Cadence</h4>
                            <p>Structured reviews and checkpoints ensure momentum and focus.</p>
                        </div>
                        <div className="cosmos-card" data-aos="fade-up" data-aos-delay="300">
                            <h4>Data-Driven Visibility</h4>
                            <p>Progress, risks, and dependencies are tracked transparently.</p>
                        </div>
                        <div className="cosmos-card" data-aos="fade-up" data-aos-delay="400">
                            <h4>Leadership Steering</h4>
                            <p>Leadership time is used to unblock and guide, not chase updates.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* COSMOS IN TRIOLOGY ENGAGEMENTS - Split */}
            <section className="section" style={{ padding: '3rem 0' }}>
                <div className="container">
                    <div className="cosmos-split-section" data-aos="fade-up">
                        <div className="cosmos-text-col">
                            <span className="cosmos-section-label">COSMOS IN TRIOLOGY ENGAGEMENTS</span>
                            <h2 className="cosmos-section-title">How We Use COSMOS With Clients</h2>
                            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                                COSMOS is embedded across all Triology engagements, especially during the Deliver stage of our consulting approach.
                            </p>
                            <p style={{ fontStyle: 'italic', color: 'var(--primary-color)', fontWeight: '600' }}>
                                COSMOS is adapted to the organization’s maturity it is not imposed rigidly.
                            </p>
                        </div>
                        <div className="cosmos-visual-col">
                            <h4 style={{ marginBottom: '1rem' }}>It helps us:</h4>
                            <ul className="cosmos-list">
                                <li>Translate designed systems into daily practices</li>
                                <li>Align multiple initiatives under one execution framework</li>
                                <li>Build execution capability within client teams</li>
                                <li>Reduce dependence on external consultants over time</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* OUTCOMES & SCALE - Grid 2 Col */}
            <section className="section cosmos-bg-light" style={{ padding: '3rem 0' }}>
                <div className="container">
                    <div className="risk-engagement-grid" style={{ maxWidth: '1400px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '4rem' }}>
                        <div data-aos="fade-right">
                            <span className="cosmos-section-label">WHAT COSMOS ENSURES</span>
                            <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>Outcomes Clients Experience</h2>
                            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                                Organizations using COSMOS experience:
                            </p>
                            <ul className="cosmos-list">
                                <li>Improved execution consistency</li>
                                <li>Faster and clearer decision-making</li>
                                <li>Stronger ownership across teams</li>
                                <li>Reduced execution fatigue</li>
                                <li>Better leadership focus and alignment</li>
                            </ul>
                            <p style={{ marginTop: '1.5rem', fontWeight: '700' }}>Most importantly, COSMOS ensures that change sticks.</p>
                        </div>

                        <div data-aos="fade-left">
                            <span className="cosmos-section-label">HOW COSMOS SUPPORTS SCALE</span>
                            <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>Execution That Grows With the Organization</h2>
                            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                                As organizations scale, execution complexity increases. COSMOS provides a stable backbone that:
                            </p>
                            <ul className="cosmos-list">
                                <li>Supports growth without chaos</li>
                                <li>Enables leaders to delegate with confidence</li>
                                <li>Maintains visibility without micromanagement</li>
                            </ul>
                            <p style={{ marginTop: '1.5rem', fontWeight: '500', color: 'var(--text-secondary)' }}>
                                This makes COSMOS particularly valuable for growth-stage organizations aspiring to enterprise-grade discipline.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final Conclusion */}
            {/* Final Conclusion - Modernized */}
            <section className="section" style={{ padding: '6rem 0', background: '#ffffff' }}>
                <div className="container">
                    <div style={{
                        maxWidth: '100%',
                        margin: '0 auto',
                        background: 'var(--bg-secondary)',
                        padding: '2rem 3rem',
                        borderRadius: '24px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.02)',
                        border: '1px solid var(--border-color)',
                        position: 'relative',
                        overflow: 'hidden'
                    }} data-aos="zoom-in">
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2.5rem' }}>
                                <div style={{ flex: '1.2', minWidth: '300px' }}>
                                    <span className="cosmos-section-label" style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>CONCLUSION</span>
                                    <h2 className="cosmos-section-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>From Plans to Performance</h2>
                                    <p style={{
                                        fontSize: '1.05rem',
                                        color: 'var(--text-secondary)',
                                        lineHeight: '1.7',
                                        margin: 0
                                    }}>
                                        COSMOS supports our catalytic role by embedding execution discipline into everyday work
                                        enabling organizations to evolve without becoming dependent on external intervention.
                                    </p>
                                    <p style={{
                                        fontSize: '1.05rem',
                                        fontWeight: '700',
                                        color: 'var(--text-primary)',
                                        marginTop: '1.25rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem'
                                    }}>
                                        <span style={{ width: '25px', height: '2px', background: 'var(--primary-color)' }}></span>
                                        At Triology, COSMOS ensures that consulting outcomes are not just delivered, but sustained.
                                    </p>
                                </div>

                                <div style={{ flex: '1', minWidth: '300px' }}>
                                    <div style={{
                                        padding: '1.75rem',
                                        background: '#ffffff',
                                        borderRadius: '16px',
                                        borderLeft: '5px solid var(--primary-color)',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.03)'
                                    }}>
                                        <p style={{
                                            fontSize: '1.2rem',
                                            fontWeight: '800',
                                            color: 'var(--text-primary)',
                                            margin: '0 0 0.5rem 0',
                                            fontStyle: 'italic',
                                            lineHeight: '1.4'
                                        }}>
                                            "COSMOS is not about doing more.
                                        </p>
                                        <p style={{
                                            fontSize: '1.2rem',
                                            fontWeight: '800',
                                            color: 'var(--primary-color)',
                                            margin: 0,
                                            fontStyle: 'italic',
                                            lineHeight: '1.4'
                                        }}>
                                            It is about executing what truly matters consistently and with clarity."
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Cosmos;
